"use client";

import { useEffect, useState } from "react";

export interface AuthSession {
  user?: {
    id?: string;
    name?: string | null;
    email?: string | null;
    image?: string | null;
    isPremium?: boolean;
  };
  expires?: string;
}

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export function useAuthSession() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const response = await fetch("/api/auth/session", {
          cache: "no-store",
          credentials: "include",
        });
        const data = await response.json().catch(() => null);

        if (!active) return;

        if (data && typeof data === "object" && "user" in data && data.user) {
          setSession(data as AuthSession);
          setStatus("authenticated");
          return;
        }
      } catch (error) {
        console.warn("Failed to load auth session:", error);
      }

      if (!active) return;
      setSession(null);
      setStatus("unauthenticated");
    };

    void loadSession();

    return () => {
      active = false;
    };
  }, []);

  return { data: session, status };
}

/**
 * Kick off Google OAuth. Pass `callbackUrl` (defaults to the current path) so
 * the user lands back where they were instead of on the homepage — important
 * for in-place sign-in prompts like the quota gate card.
 *
 * Uses next-auth's `signIn`, which fetches a CSRF token and POSTs to the
 * provider route. Navigating to that route with a plain GET — which is what
 * this used to do — does NOT start OAuth: NextAuth answers a GET by rendering
 * its own unbranded "Sign In" page, so the user lands on a bare white page and
 * has to press a second, identical-looking button before anything happens.
 * GA4 recorded people clicking our button and no accounts ever being created.
 *
 * `signIn` works without a SessionProvider — it is a standalone helper, unlike
 * `useSession` (which is why session state here is hand-rolled above).
 */
export async function startGoogleSignIn(callbackUrl?: string) {
  const target =
    callbackUrl ?? `${window.location.pathname}${window.location.search}`;

  try {
    const { signIn } = await import("next-auth/react");
    await signIn("google", { callbackUrl: target });
  } catch {
    // Chunk load or CSRF fetch failed. Fall back to the old GET, which lands on
    // NextAuth's own sign-in page: one extra click, but far better than a
    // button that does nothing. Callers treat this as fire-and-forget, so a
    // rejected promise here would surface as an unhandled rejection instead.
    window.location.href = `/api/auth/signin/google?callbackUrl=${encodeURIComponent(target)}`;
  }
}

export function startSignOut() {
  window.location.href = "/api/auth/signout";
}
